import moment from "moment";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { dateOnlyFormatted } from "../../../utils/systemSettings";
import { getTaskStatus } from "../utils/helper";
import {
  CompletedTaskIcon,
  InProgressTaskIcon,
  SlippingTaskIcon,
  MonitoringTaskIcon,
  MilestoneIcon,
  OnHoldTaskIcon,
} from "../icons/icons";

const taskStatusMapper = {
  Inprogress: "In Progress",
  Monitoring: "Monitoring",
  Slipping: "Slipping",
  Completed: "Completed",
  Onhold: "On Hold",
};

const getStatus = (status) => taskStatusMapper[status] ?? "";

const Icons = {
  3: () => (
    <div className="flex items-center h-[12px] w-[12px]">
      <CompletedTaskIcon size={16} color="#A0A0A0" />
    </div>
  ),
  0: () => (
    <div className="flex items-center h-[12px] w-[12px]">
      <InProgressTaskIcon size={16} color="#A0A0A0" />
    </div>
  ),
  2: () => (
    <div className="flex items-center h-[12px] w-[12px]">
      <SlippingTaskIcon size={16} color="#A0A0A0" />
    </div>
  ),
  1: () => (
    <div className="flex items-center h-[12px] w-[12px]">
      <MonitoringTaskIcon size={16} color="#A0A0A0" />
    </div>
  ),
  4: () => (
    <div className="flex items-center h-[12px] w-[12px]">
      <OnHoldTaskIcon size={16} color="#AAAAAA" />
    </div>
  ),
};

export const BaselineTask = ({
   critical, 
   left, 
   width, 
   task, 
   baseline,
   isHideTask = false
   }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [setting] = useState(settings);
  const textRef = useRef(null);
  const [textWidth, setTextWidth] = useState(0);
  
  useEffect(() => {
    if (textRef.current) {
      setTextWidth(textRef.current.getBoundingClientRect().width);
    }
  }, [task.name, baseline?.date]);

  return (
    <div
      className="absolute top-1/2 -translate-y-1/2 h-6 flex flex-row items-center justify-between select-none"
      style={{
        left: `${left - textWidth - 3}px`,
      }}
    >
      {/* Baseline info + Task Name Text */}
      <span
        ref={textRef}
        className={`text-xs text-left whitespace-nowrap pr-2 pointer-events-none text-gray-500 ${isHideTask && 'invisible'}`}
      >
        baseline {moment(baseline?.date).format("YYMMDD")} <span>•</span> {task.name}
      </span>

      {/* Baseline Bar */}
      <div
        className="relative cursor-pointer"
        style={{
          width: `${width}px`,
        }}
        onMouseEnter={(e) => {
          setTooltipVisible(true);
          setTooltipPosition({ x: e.clientX, y: e.clientY });
        }}
        onMouseLeave={() => {
          setTooltipVisible(false);
          setTooltipPosition({ x: 0, y: 0 });
        }}
        onMouseMove={(e) => setTooltipPosition({ x: e.clientX, y: e.clientY })}
      >
        <div className="flex justify-between items-center rounded w-full px-1 h-[16px] bg-gray-200">
          <div className="flex items-center justify-start gap-1 w-full overflow-hidden">
            {width > 20 && (
              <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center">
                {Icons[task.status]()}
              </div>
            )}
            <span className="font-medium text-xs capitalize text-center truncate text-gray-500">
              {getStatus(getTaskStatus(task.status, task.isCritical, critical))}
            </span>
          </div>
        </div>
      </div>

      {/* Milestone indicator */}
      {task?.isMilestone && (
        <div className="ml-1">
          <MilestoneIcon size={16} color="#A0A0A0" />
        </div>
      )}

      {/* End date */}
      {task?.endDate && (
        <span className="pl-1 text-xs whitespace-nowrap pointer-events-none text-gray-500">
          {moment(task?.endDate).format("DD-MM")}
        </span>
      )}

      {tooltipVisible &&
        createPortal(
          <div
            className="fixed z-[200] bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none"
            style={{
              top: `${tooltipPosition.y + 10}px`,
              left: `${tooltipPosition.x + 10}px`,
              maxWidth: "200px",
              whiteSpace: "nowrap",
            }}
          >
            <div className="font-semibold mb-2">Baseline: {moment(baseline?.date).format("YYMMDD")}</div>
            <div className="font-semibold">{task.name}</div>
            <div className="font-semibold">
              Start date: {dateOnlyFormatted(setting, task.startDate)}
            </div>
            <div className="font-semibold">
              End date: {dateOnlyFormatted(setting, task.endDate)}
            </div>
            <div>
              Status: {getTaskStatus(task.status, task.isCritical, critical)}
            </div>
            <div>Dependencies: {task.dependencies?.length || 0}</div>
          </div>,
          document.body
        )}
    </div>
  );
};
